import { OnChangeFn, SortingState } from "@tanstack/react-table";
import { debounce } from "lodash";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useReducer } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { listCases } from "app/actions/caseActions";
import { useAppDispatch } from "app/hooks";
import { useSelectedLabStation } from "hooks/useSelectedLabStation";

import {
  CaseAccessionFiltersState,
  DEFAULT_ORDERING,
  defaultCaseAccessionFilters,
} from "./CaseAccessionFilters.contracts";
import { createOrderingParam } from "./CaseAccessionFilters.utils";

function reducer(
  state: CaseAccessionFiltersState,
  action: { type: string; newValue: any }
): CaseAccessionFiltersState {
  switch (action.type) {
    case "status":
      return { ...state, status: action.newValue };
    case "clinicId":
      return { ...state, clinicId: action.newValue };
    case "collectedAt":
      return { ...state, collectedAt: action.newValue };
    case "search":
      return { ...state, search: action.newValue };
    case "pageCount":
      return { ...state, pageCount: action.newValue };
    case "pageSize":
      return { ...state, pageSize: action.newValue, currentPage: 1 };
    case "currentPage":
      return { ...state, currentPage: action.newValue };
    case "sorting":
      return { ...state, sorting: action.newValue };
    case "clear":
      return defaultCaseAccessionFilters;
    default:
      return state;
  }
}

const useCaseAccessionFilters = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const station = useSelectedLabStation();
  const searchParams = new URLSearchParams(location.search);
  const [filters, dispatchFilters] = useReducer(reducer, {
    status: searchParams.get("status") || undefined,
    clinicId: searchParams.get("clinicId")
      ? parseInt(searchParams.get("clinicId") || "")
      : undefined,
    collectedAt: searchParams.get("collectedAt") || undefined,
    search: searchParams.get("search") || "",
    pageCount: 0,
    pageSize: 10,
    currentPage: parseInt(searchParams.get("page") || "1"),
    sorting: [
      {
        id: "specimens__collected_date",
        desc: true,
      },
    ],
  } as CaseAccessionFiltersState);

  // Sorting is a special case because tanstack table wants the old value to compute the new one
  const setSorting: OnChangeFn<SortingState> = (v) => {
    const type = "sorting";
    // @ts-ignore
    const temp = v(filters.sorting);
    const newValue = !temp?.length
      ? [
          {
            id: "specimens__collected_date",
            desc: false,
          },
        ]
      : temp;
    const newState = { ...filters, [type]: newValue };
    const orderingParam = createOrderingParam(newState.sorting);
    dispatchApi(newState, orderingParam);
    updateUrl(newState, orderingParam);
    dispatchFilters({ type, newValue });
  };

  const orderingParam = useMemo(
    () => createOrderingParam(filters.sorting),
    [filters.sorting]
  );

  const updateFilters = (type: string, newValue: any) => {
    const newState = { ...filters, [type]: newValue };
    dispatchApi(newState);
    updateUrl(newState);
    dispatchFilters({ type, newValue });
  };

  const updateFiltersWithDebounce = (type: string, newValue: any) => {
    const newState = { ...filters, [type]: newValue };
    dispatchWithDebounce(newState);
    dispatchFilters({ type, newValue });
  };

  const dispatchApi = useCallback(
    (newState, subOrderingParam?: string) => {
      const apiOrderingParam =
        subOrderingParam || orderingParam || DEFAULT_ORDERING;
      dispatch(
        listCases({
          parentLookupLabStationId: station.id,
          limit: newState.pageSize,
          offset: (newState.currentPage - 1) * newState.pageSize,
          ordering: apiOrderingParam,
          collectedAtStart:
            DateTime.fromISO(newState.collectedAt).startOf("day").toISO() ||
            undefined,
          collectedAtEnd:
            DateTime.fromISO(newState.collectedAt).endOf("day").toISO() ||
            undefined,
          status: newState.status,
          ...(newState.clinicId && !isNaN(newState.clinicId)
            ? { clinicId: newState.clinicId }
            : {}),
          search: newState.search,
        })
      ).then((response) => {
        dispatchFilters({
          type: "pageCount",
          newValue: Math.ceil(response.count / filters.pageSize),
        });
      });
    },
    [station.id, orderingParam]
  );

  useEffect(() => {
    dispatchApi(filters);
  }, []);

  const updateUrl = useCallback(
    (newState: CaseAccessionFiltersState, subOrderingParam?: string) => {
      const urlOrderingParam = subOrderingParam || orderingParam;
      const params = {
        ...(newState.search && { search: newState.search }),
        ...(newState.clinicId && { clinicId: newState.clinicId.toString() }),
        ...(newState.collectedAt && { collectedAt: newState.collectedAt }),
        ...(newState.status && { status: newState.status }),
        ...(newState.currentPage > 1 && {
          page: newState.currentPage.toString(),
        }),
        ...(urlOrderingParam &&
          urlOrderingParam !== "-specimens__collected_date" && {
            ordering: urlOrderingParam,
          }),
      } as Record<string, string>;

      navigate(`/cases?${new URLSearchParams(params).toString()}`, {
        replace: true,
      });
    },
    [orderingParam]
  );

  const dispatchWithDebounce = useCallback(
    debounce((newState: CaseAccessionFiltersState) => {
      dispatchApi(newState);
      updateUrl(newState);
    }, 1000),
    [dispatchApi, updateUrl]
  );

  const clearFilters = useCallback(() => {
    const clearedOrderingParam = createOrderingParam(
      defaultCaseAccessionFilters.sorting
    );
    dispatchFilters({ type: "clear", newValue: null });
    dispatchApi(defaultCaseAccessionFilters, clearedOrderingParam);
    updateUrl(defaultCaseAccessionFilters, clearedOrderingParam);
  }, [dispatchApi, updateUrl]);

  return {
    filters,
    updateFilters,
    updateFiltersWithDebounce,
    clearFilters,
    setSorting,
  };
};

export default useCaseAccessionFilters;
